import resolveFrontendModule from "@app/resolveModule/frontend";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import { downloadFile } from "@core-ui/downloadResource";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import type {
	BillingModeCode,
	BillingPeriodCode,
	GesCloudPaymentInfo,
	GesCloudSubscription,
	PaymentChannelCode,
} from "@ext/enterprise-cloud/GesCloudApi";
import { getAcquiringReturnUrl } from "@ext/enterprise-cloud/utils/getAcquiringReturnUrl";
import t from "@ext/localization/locale/translate";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField, FormStack } from "@ui-kit/Form";
import { InputGroup, InputGroupButton, InputGroupInput } from "@ui-kit/Input";
import { Skeleton } from "@ui-kit/Skeleton";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { showPopover } from "../../../../ui-logic/showPopover";
import DateUtils from "../../../../ui-logic/utils/dateUtils";
import {
	ButtonWithLoadingState,
	formatCurrencyWithSymbol,
	OfferAcceptanceNotice,
	PayModalLayout,
	PayModalSummary,
	PayModalSummarySeparator,
	PaymentInfoLoader,
	Row,
} from "./paymentModalComponents";
import { usePayModalState } from "./usePayModalState";

interface SeatStepperProps {
	value: number;
	min: number;
	disabled?: boolean;
	name?: string;
	onBlur?: () => void;
	onDecrement: () => void;
	onIncrement: () => void;
	onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const SeatStepper = ({
	value,
	min,
	disabled,
	name,
	onBlur,
	onDecrement,
	onIncrement,
	onInputChange,
}: SeatStepperProps) => (
	<InputGroup className="w-[148px] max-w-full">
		<InputGroupButton disabled={disabled || value <= min} icon="minus" onClick={onDecrement} type="button" />
		<InputGroupInput
			className="min-w-0 text-center font-semibold [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
			disabled={disabled}
			min={min}
			name={name}
			onBlur={onBlur}
			onChange={onInputChange}
			step={1}
			type="number"
			value={value}
		/>
		<InputGroupButton disabled={disabled} icon="plus" onClick={onIncrement} type="button" />
	</InputGroup>
);

interface PurchaseSeatCountFieldProps {
	form: ReturnType<typeof useForm<{ addedSeatsCount?: number }>>;
	seatsCount: number;
	minAddedSeatsCount: number;
	disabled?: boolean;
	onDecrementSeats: (value: number, onChange: (value: number) => void) => void;
	onIncrementSeats: (value: number, onChange: (value: number) => void) => void;
	onSeatsInputChange: (event: ChangeEvent<HTMLInputElement>, onChange: (value: number) => void) => void;
}

const PurchaseSeatCountField = ({
	form,
	seatsCount,
	minAddedSeatsCount,
	disabled,
	onDecrementSeats,
	onIncrementSeats,
	onSeatsInputChange,
}: PurchaseSeatCountFieldProps) => (
	<Form asChild {...form}>
		<form className="flex w-full min-w-0 items-center justify-between gap-4">
			<div className="flex min-w-0 flex-col gap-1">
				<span className="font-medium text-base">
					{t("enterprise-cloud.org-settings.subscription.payment.set-seats-count")}
				</span>
				<span className="truncate text-muted-foreground set-seats-count text-sm">
					{t("enterprise-cloud.org-settings.subscription.payment.new-seats-count").replace(
						"{newSeatsCount}",
						String(seatsCount),
					)}
				</span>
			</div>
			<FormStack className="shrink-0">
				<FormField
					control={({ field }) => (
						<SeatStepper
							disabled={disabled}
							min={minAddedSeatsCount}
							name={field.name}
							onBlur={field.onBlur}
							onDecrement={() => onDecrementSeats(field.value, field.onChange)}
							onIncrement={() => onIncrementSeats(field.value, field.onChange)}
							onInputChange={(event) => onSeatsInputChange(event, field.onChange)}
							value={field.value}
						/>
					)}
					labelClassName="sr-only"
					layout="vertical"
					name="addedSeatsCount"
					title={t("enterprise-cloud.org-settings.subscription.payment.set-seats-count")}
				/>
			</FormStack>
		</form>
	</Form>
);

interface PurchaseSeatSummaryProps {
	isLoading?: boolean;
	paymentInfo: GesCloudPaymentInfo;
	addedSeatsCount: number;
}

const PurchaseSeatSummary = ({ isLoading, paymentInfo, addedSeatsCount }: PurchaseSeatSummaryProps) => {
	const totalLabel = paymentInfo.nextChargeAt
		? paymentInfo.billingPeriod === "year"
			? t("enterprise-cloud.org-settings.subscription.payment.yearly")
			: t("enterprise-cloud.org-settings.subscription.payment.monthly")
		: paymentInfo.billingPeriod === "year"
			? t("enterprise-cloud.org-settings.subscription.payment.total-yearly")
			: t("enterprise-cloud.org-settings.subscription.payment.total-monthly");
	const payNowAmount = formatCurrencyWithSymbol(paymentInfo.payNowAmount, paymentInfo.currency);
	const periodicPaymentLabel = paymentInfo.nextChargeAt
		? t("enterprise-cloud.org-settings.subscription.payment.periodic-payment-from")
				.replace("{periodicPayment}", totalLabel)
				.replace("{date}", DateUtils.formatDate(paymentInfo.nextChargeAt, { dateStyle: "short" }))
		: totalLabel;
	const payNowSeatsDescription = paymentInfo.nextChargeAt
		? t("enterprise-cloud.org-settings.subscription.payment.due-now-seats-until-next-charge")
				.replace("{seatsCount}", String(addedSeatsCount))
				.replace("{daysCount}", String(getApproximateDaysUntilDate(paymentInfo.nextChargeAt)))
				.replace("{date}", DateUtils.formatDate(paymentInfo.nextChargeAt, { dateStyle: "short" }))
		: `${t("enterprise-cloud.org-settings.subscription.payment.due-now-seats")}`;
	const pricePerSeat = Number(paymentInfo.paymentPerPeriod) / Math.max(paymentInfo.editorsCount, 1);

	return (
		<PayModalSummary>
			<div className="flex flex-col gap-2">
				{isLoading ? (
					<Skeleton className="w-full h-5" />
				) : (
					<Row
						accent="primary"
						label={t("enterprise-cloud.org-settings.subscription.payment.due-today-title")}
						value={payNowAmount}
					/>
				)}
				{isLoading ? (
					<Skeleton className="w-full h-5" />
				) : (
					<Row accent="muted" label={payNowSeatsDescription} />
				)}
			</div>

			<PayModalSummarySeparator />

			<div className="flex flex-col gap-2">
				{isLoading ? (
					<Skeleton className="w-full h-5" />
				) : (
					<Row
						label={periodicPaymentLabel}
						value={formatCurrencyWithSymbol(paymentInfo.paymentPerPeriod, paymentInfo.currency)}
					/>
				)}
				{isLoading ? (
					<Skeleton className="w-full h-5" />
				) : (
					<Row
						accent="muted"
						label={`${paymentInfo.editorsCount} × ${formatCurrencyWithSymbol(pricePerSeat, paymentInfo.currency)}`}
					/>
				)}
			</div>
		</PayModalSummary>
	);
};

export interface PurchaseSeatPayModalProps {
	minAddedSeatsCount: number;
	currentSeatsCount: number;
	billingPeriod?: BillingPeriodCode;
	billingMode: BillingModeCode;
	subscription: GesCloudSubscription;
	onClose: () => void;
	onSuccess?: () => void;
}

export const PurchaseSeatPayModal = ({
	minAddedSeatsCount,
	currentSeatsCount,
	billingPeriod,
	billingMode,
	subscription,
	onClose,
	onSuccess,
}: PurchaseSeatPayModalProps) => {
	const formSchema = z.object({
		addedSeatsCount: z
			.number()
			.int()
			.positive()
			.gte(
				minAddedSeatsCount,
				t("enterprise-cloud.org-settings.subscription.payment.not-enough-seats-error").replace(
					"{minSeatsCount}",
					String(minAddedSeatsCount),
				),
			)
			.step(1),
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		mode: "onChange",
		defaultValues: {
			addedSeatsCount: minAddedSeatsCount,
		},
	});

	const { isTauri } = usePlatform();
	const { gesCloudApi, open, error, setError, onOpenChangeHandler } = usePayModalState(onClose);
	const [paymentInfo, setPaymentInfo] = useState<GesCloudPaymentInfo | null>(null);
	const [paymentChannelInProgress, setPaymentChannelInProgress] = useState<PaymentChannelCode | null>(null);
	const [loadingState, setLoadingState] = useState<{ isLoading: boolean; seatsCount: number } | null>(null);
	const addedSeatsCount = form.watch("addedSeatsCount");
	const seatsCount = currentSeatsCount + addedSeatsCount;
	const { isValid } = form.formState;
	const onDecrementSeats = useCallback(
		(value: number, onChange: (value: number) => void) => {
			if (value > minAddedSeatsCount) onChange(value - 1);
		},
		[minAddedSeatsCount],
	);
	const onIncrementSeats = useCallback((value: number, onChange: (value: number) => void) => {
		onChange(value + 1);
	}, []);
	const onSeatsInputChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>, onChange: (value: number) => void) => {
			if (event.target.value === "") return;
			const value = Number(event.target.value);
			if (!Number.isFinite(value)) return;
			onChange(Math.max(minAddedSeatsCount, Math.trunc(value)));
		},
		[minAddedSeatsCount],
	);

	const idempotenceKeyRef = useRef<{
		key: string;
		seatsCount: number;
		billingPeriod: BillingPeriodCode | undefined;
		paymentChannel: PaymentChannelCode;
	} | null>(null);

	const getIdempotenceKey = useCallback(
		(paymentChannel: PaymentChannelCode) => {
			const current = idempotenceKeyRef.current;
			if (
				current &&
				current.seatsCount === seatsCount &&
				current.billingPeriod === billingPeriod &&
				current.paymentChannel === paymentChannel
			) {
				return current.key;
			}

			const key = globalThis.crypto.randomUUID();
			idempotenceKeyRef.current = { key, seatsCount, billingPeriod, paymentChannel };
			return key;
		},
		[seatsCount, billingPeriod],
	);

	useEffect(() => {
		const abortController = new AbortController();

		const loadPaymentInfo = async () => {
			setError(null);

			if (isValid) {
				setLoadingState({ isLoading: true, seatsCount });
				try {
					const data = await gesCloudApi.getPrePurchaseSeatsInfo(
						seatsCount,
						billingPeriod,
						abortController.signal,
					);
					setPaymentInfo(data);
				} catch {
					if (!abortController.signal.aborted)
						setError(t("enterprise-cloud.org-settings.subscription.payment.load-error"));
				} finally {
					setLoadingState((prev) => {
						if (prev?.seatsCount === seatsCount) return { isLoading: false, seatsCount };
						return prev;
					});
				}
			}
		};

		void loadPaymentInfo();

		return () => abortController.abort();
	}, [gesCloudApi, isValid, seatsCount, billingPeriod, setError]);

	const onPay = useCallback(
		async (paymentChannel: PaymentChannelCode) => {
			if (paymentChannelInProgress || !isValid) return;

			setPaymentChannelInProgress(paymentChannel);
			setError(null);
			try {
				const result = await gesCloudApi.purchaseSeat(getIdempotenceKey(paymentChannel), {
					totalSeatsCount: seatsCount,
					billingPeriod,
					returnUrl: getAcquiringReturnUrl(isTauri),
					paymentChannel,
				});

				if (result.status === "redirect" && result.redirectUrl) {
					if (isTauri) {
						await resolveFrontendModule("gesCloudOpenAcquiring")(result.redirectUrl);
					} else window.open(result.redirectUrl, "_blank");
					onClose();
					return;
				}

				if (result.status === "invoice") {
					const invoice = await gesCloudApi.getInvoicePdf(result.invoiceId);
					downloadFile(invoice.data, MimeTypes.pdf, invoice.fileName);
					onClose();
					return;
				}

				if (result.status === "reserved") onSuccess?.();

				onClose();
				showPopover(t("enterprise-cloud.org-settings.subscription.payment.autopay-success"));
			} catch {
				setError(t("enterprise-cloud.org-settings.subscription.payment.pay-error"));
				setPaymentChannelInProgress(null);
			}
		},
		[
			gesCloudApi,
			isValid,
			seatsCount,
			billingPeriod,
			onClose,
			onSuccess,
			getIdempotenceKey,
			paymentChannelInProgress,
			setError,
			isTauri,
		],
	);

	const onCreateInvoice = useCallback(() => void onPay("bank_transfer"), [onPay]);
	const onPayByCard = useCallback(() => void onPay("online_payment"), [onPay]);

	const paymentDisabled =
		!paymentInfo || Boolean(paymentChannelInProgress) || !isValid || Boolean(loadingState?.isLoading);
	const isCreatingInvoice = paymentChannelInProgress === "bank_transfer";
	const isPayingByCard = paymentChannelInProgress === "online_payment";
	const isInitialSubscription = subscription.plan === "free";
	const payNowAmount = paymentInfo ? formatCurrencyWithSymbol(paymentInfo.payNowAmount, paymentInfo.currency) : "";
	const payButtonText = t("enterprise-cloud.org-settings.subscription.payment.pay-amount").replace(
		"{amount}",
		payNowAmount,
	);

	return (
		<PayModalLayout
			description={t("enterprise-cloud.org-settings.subscription.payment.description")}
			error={error}
			onOpenChange={onOpenChangeHandler}
			open={open}
			title={t("enterprise-cloud.org-settings.subscription.payment.title")}
		>
			{paymentInfo ? (
				<>
					<PurchaseSeatCountField
						disabled={Boolean(paymentChannelInProgress)}
						form={form}
						minAddedSeatsCount={minAddedSeatsCount}
						onDecrementSeats={onDecrementSeats}
						onIncrementSeats={onIncrementSeats}
						onSeatsInputChange={onSeatsInputChange}
						seatsCount={seatsCount}
					/>
					<PurchaseSeatSummary
						addedSeatsCount={addedSeatsCount}
						isLoading={loadingState?.isLoading}
						paymentInfo={paymentInfo}
					/>
				</>
			) : (
				<PaymentInfoLoader />
			)}

			{billingMode === "legal_entity" ? (
				<div className="flex flex-col gap-2">
					<ButtonWithLoadingState
						disabled={paymentDisabled}
						isLoading={isCreatingInvoice}
						onClick={onCreateInvoice}
						text={t("enterprise-cloud.org-settings.subscription.payment.create-invoice")}
						variant="outline"
					/>
					<ButtonWithLoadingState
						disabled={paymentDisabled}
						isLoading={isPayingByCard}
						onClick={onPayByCard}
						text={t("enterprise-cloud.org-settings.subscription.payment.pay-by-card")}
						variant="outline"
					/>
				</div>
			) : (
				<>
					<ButtonWithLoadingState
						disabled={paymentDisabled}
						isLoading={isPayingByCard}
						onClick={onPayByCard}
						text={payButtonText}
						variant="primary"
					/>
					{paymentInfo && (
						<div className="flex flex-col gap-1 text-muted-foreground text-center text-sm">
							{isInitialSubscription && (
								<span>{t("enterprise-cloud.org-settings.subscription.payment.autorenew-notice")}</span>
							)}
							{!subscription.hasPaymentMethod && (
								<span>
									{t("enterprise-cloud.org-settings.subscription.payment.payment-method-save-notice")}
								</span>
							)}
							<OfferAcceptanceNotice />
						</div>
					)}
				</>
			)}
		</PayModalLayout>
	);
};

const DAY_MS = 24 * 60 * 60 * 1000;

const getApproximateDaysUntilDate = (date: string) =>
	Math.max(0, Math.ceil((new Date(date).getTime() - Date.now()) / DAY_MS));
